import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import axios from 'axios';

import "./BannerSlide.css";

interface BannersDetails {
  bannerId: string;
  bannerText: string;
  bannerImageName: string;
}

interface ImageLoadState {
  loaded: boolean;
  error: boolean;
}

export const BannerSlide: React.FC = React.memo(() => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [bannerData, setBannerData] = useState<BannersDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoadStates, setImageLoadStates] = useState<Map<string, ImageLoadState>>(new Map());
  const [currentImageLoaded, setCurrentImageLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const preloadedImagesRef = React.useRef<Set<string>>(new Set());
  const { instance, accounts } = useMsal();

  const API_URL = useMemo(() => process.env.REACT_APP_API_URL, []);

  // Generate optimized image URLs with proper public path including homepage prefix
  const getImageUrl = useCallback((imageName: string, quality: 'low' | 'high' = 'high') => {
    if (!imageName) return '';
    
    // Use process.env.PUBLIC_URL to handle homepage configuration
    const publicUrl = process.env.PUBLIC_URL || '';
    const basePath = `${publicUrl}/images/TopBanner/TopMiddleBanner/`;
    
    if (quality === 'low') {
      // For low quality, just return the same image for now since thumbs don't exist
      // Could implement base64 tiny placeholders later
      return `${basePath}${imageName}`;
    }
    
    return `${basePath}${imageName}`;
  }, []);

  // Optimized image preloading - only preload current and next image
  const preloadImage = useCallback((imageName: string, priority: 'high' | 'low' = 'low') => {
    // Prevent duplicate preloading
    if (preloadedImagesRef.current.has(imageName)) {
      return;
    }
    
    const imageUrl = getImageUrl(imageName);
    preloadedImagesRef.current.add(imageName);
    
    setImageLoadStates(prev => {
      const newStates = new Map(prev);
      if (!newStates.has(imageName)) {
        newStates.set(imageName, { loaded: false, error: false });
        
        const img = new Image();
        img.onload = () => {
          setImageLoadStates(current => {
            const updated = new Map(current);
            updated.set(imageName, { loaded: true, error: false });
            return updated;
          });
        };
        img.onerror = (error) => {
          console.error('Image failed to load:', imageUrl, error);
          
          // Try fallback path without PUBLIC_URL prefix
          if (imageUrl.includes(process.env.PUBLIC_URL || '')) {
            const fallbackUrl = imageUrl.replace(process.env.PUBLIC_URL || '', '');
            console.log('Trying fallback URL:', fallbackUrl);
            
            const fallbackImg = new Image();
            fallbackImg.onload = () => {
              setImageLoadStates(current => {
                const updated = new Map(current);
                updated.set(imageName, { loaded: true, error: false });
                return updated;
              });
            };
            fallbackImg.onerror = () => {
              setImageLoadStates(current => {
                const updated = new Map(current);
                updated.set(imageName, { loaded: false, error: true });
                return updated;
              });
            };
            fallbackImg.src = fallbackUrl;
          } else {
            setImageLoadStates(current => {
              const updated = new Map(current);
              updated.set(imageName, { loaded: false, error: true });
              return updated;
            });
          }
        };
        
        // Set src last for better loading performance
        img.src = imageUrl;
      }
      return newStates;
    });
  }, [getImageUrl]);
    // Shuffle array function for random banner display
    const shuffleArray = <T,>(array: T[]): T[] => {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };
    const fetchToken = useCallback(async () => {
      try {
        if (!accounts[0]) throw new Error('No account found');
        const res = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
        return res.idToken;
      } catch (error) {
        console.error('Token fetch failed:', error);
        throw error;
      }
    }, [instance, accounts]);

    const fetchData = useCallback(async () => {
      try {
        setIsLoading(true);
        
        // Fetch fresh data from API without caching
        const token = await fetchToken();
        const response = await axios.get(`${API_URL}/api/Banner/GetBanners/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 30000,
          validateStatus: (status) => status >= 200 && status < 300
        });
        
        if (response.status === 200 && response.data) {
          // Filter to show only active banners
          const activeBanners = response.data.filter((banner: any) => banner.isActive === true);
          
          // Shuffle banners for random display
          const shuffledBanners = shuffleArray<BannersDetails>(activeBanners || []);
          setBannerData(shuffledBanners);
          
          // Reset preloaded images tracking when new data is fetched
          preloadedImagesRef.current.clear();
        } else {
          throw new Error('Invalid response');
        }
        
      } catch (error) {
        console.error('Banner fetch error:', error);
        setBannerData([]);
      } finally {
        setIsLoading(false);
      }
    }, [API_URL, fetchToken]);

    // Intersection Observer for performance optimization
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
        },
        {
          threshold: 0.1,
          rootMargin: '50px',
        }
      );

      if (carouselRef.current) {
        observer.observe(carouselRef.current);
      }

      return () => observer.disconnect();
    }, []);

    useEffect(() => {
      // Only fetch data when accounts are available
      if (accounts && accounts.length > 0) {
        fetchData();
      }
    }, [fetchData, accounts]);

    // Preload next image when index or bannerData changes
    useEffect(() => {
      if (bannerData.length > 0) {
        const currentImage = bannerData[index];
        const nextIndex = (index + 1) % bannerData.length;
        const nextImage = bannerData[nextIndex];
        
        // Preload current image with high priority if not already loaded
        if (currentImage && !preloadedImagesRef.current.has(currentImage.bannerImageName)) {
          preloadImage(currentImage.bannerImageName, 'high');
        }
        
        // Preload next image with low priority
        if (nextImage && !preloadedImagesRef.current.has(nextImage.bannerImageName)) {
          preloadImage(nextImage.bannerImageName, 'low');
        }
      }
    }, [index, bannerData, preloadImage]);
    
    // Track current image load state
    useEffect(() => {
      if (bannerData.length > 0) {
        const currentImage = bannerData[index];
        const currentState = imageLoadStates.get(currentImage.bannerImageName);
        setCurrentImageLoaded(currentState?.loaded ?? false);
      }
    }, [bannerData, index, imageLoadStates]);

    useEffect(() => {
      // Only run slideshow when component is visible and has data
      if (bannerData.length > 0 && isVisible) {
        const interval = setInterval(() => {
          setDirection(1); // Set direction to forward
          setIndex((prevIndex) => (prevIndex + 1) % bannerData.length); // Loop back to the first banner
        }, 4000); // Change slide every 4 seconds
    
        return () => clearInterval(interval); // Cleanup interval on unmount
      }
    }, [bannerData, isVisible]);

  // Show loading state with skeleton
  if (isLoading) {
    return (
      <div className="custom-carousel loading-state">
        <div className="loading-skeleton">
          <div className="skeleton-image"></div>
          <div className="loading-text">Loading banners...</div>
        </div>
      </div>
    );
  }

  // Show message if no banners available
  if (bannerData.length === 0) {
    return (
      <div className="custom-carousel no-banners">
        <div className="no-content-placeholder">
          No banners available
        </div>
      </div>
    );
  }

  const currentBanner = bannerData[index];
  const currentImageState = imageLoadStates.get(currentBanner.bannerImageName);

  return (
    <div ref={carouselRef} className="custom-carousel">
      {/* Progressive loading with blur effect */}
      {!currentImageLoaded && (
        <div className="progressive-image-container">
          {/* Low quality placeholder with blur */}
          <img
            src={getImageUrl(currentBanner.bannerImageName, 'low')}
            alt=""
            className="carousel-image-placeholder"
            style={{
              filter: 'blur(4px)',
              transform: 'scale(1.05)', // Slightly larger to hide blur edges
              opacity: 0.6
            }}
            onError={() => {
              // Fallback to skeleton if low-quality image fails
            }}
          />
          <div className="image-loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        </div>
      )}
      
      <AnimatePresence mode="wait" custom={direction}>
        <motion.img
          key={`${index}-${currentBanner.bannerId}`}
          src={getImageUrl(currentBanner.bannerImageName)}
          alt={currentBanner.bannerText || `Banner ${index + 1}`}
          className="carousel-image"
          loading="eager" // Eager loading for current image
          decoding="async" // Async decoding for performance
          style={{ 
            willChange: 'transform, opacity',
            backfaceVisibility: 'hidden', // GPU optimization
            opacity: currentImageLoaded ? 1 : 0,
            transition: 'opacity 0.4s ease-in-out, filter 0.3s ease-in-out',
            filter: currentImageLoaded ? 'none' : 'blur(1px)'
          }}
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ 
            scale: 1, 
            opacity: currentImageLoaded ? 1 : 0
          }}
          exit={{ scale: 1.02, opacity: 0 }}
          transition={{ 
            duration: currentImageLoaded ? 0.2 : 0.4, 
            ease: "easeOut" 
          }}
          onLoad={() => {
            setCurrentImageLoaded(true);
            setImageLoadStates(prev => {
              const updated = new Map(prev);
              updated.set(currentBanner.bannerImageName, { loaded: true, error: false });
              return updated;
            });
          }}
          onError={(e) => {
            const failedUrl = getImageUrl(currentBanner.bannerImageName);
            console.error('Main image failed to load:', {
              imageName: currentBanner.bannerImageName,
              fullUrl: failedUrl,
              publicUrl: process.env.PUBLIC_URL,
              error: e
            });
            
            // Try fallback URL without PUBLIC_URL prefix
            if (failedUrl.includes(process.env.PUBLIC_URL || '')) {
              const fallbackUrl = `/images/TopBanner/TopMiddleBanner/${currentBanner.bannerImageName}`;
              console.log('Trying fallback URL for main image:', fallbackUrl);
              e.currentTarget.src = fallbackUrl;
            } else {
              setImageLoadStates(prev => {
                const updated = new Map(prev);
                updated.set(currentBanner.bannerImageName, { loaded: false, error: true });
                return updated;
              });
              // Hide broken image and show error state
              e.currentTarget.style.display = 'none';
            }
          }}
        />
      </AnimatePresence>
    </div>
  );
});